import { useEffect, useState } from 'react';
import { useLocation } from '@remix-run/react';
import { toast } from 'react-toastify';
import { useTranslation } from '~/lib/hooks/useTranslation';

// Browser detection utility
const detectSafari = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor;

  // console.log('[SafariBlockModal] userAgent:', userAgent);
  // console.log('[SafariBlockModal] vendor:', vendor);
  //  return true;
  // Comprehensive Safari detection (including mobile Safari)
  return (
    /Safari/.test(userAgent) &&
    !/Chrome/.test(userAgent) &&
    !/Chromium/.test(userAgent) &&
    !/Edge/.test(userAgent) &&
    !/OPR/.test(userAgent) && // Opera
    !/Firefox/.test(userAgent) && // Firefox
    (vendor === 'Apple Computer, Inc.' || /Apple/.test(vendor))
  );
};

// Chrome URL schemes for different platforms
const getChromeUrlScheme = (url: string): string => {
  const cleanUrl = url.replace(/^https?:\/\//, '');

  if (navigator.platform.includes('Mac')) {
    return `googlechrome://${cleanUrl}`;
  } else if (navigator.platform.includes('Win')) {
    return `chrome://${cleanUrl}`;
  } else {
    return `google-chrome://${cleanUrl}`;
  }
};

export function SafariBlockModal() {
  const { t } = useTranslation();
  const [isSafari, setIsSafari] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const onChatRoute = location?.pathname?.startsWith('/chat') || location?.pathname === '/';

  useEffect(() => {
    const safariDetected = detectSafari();
    setIsSafari(safariDetected);
    setIsModalOpen(safariDetected && !!onChatRoute);
  }, [onChatRoute]);

  const handleOpenInChrome = async () => {
    const currentUrl = window.location.href;
    console.log('[SafariBlockModal] handleOpenInChrome called with URL:', currentUrl);

    try {
      // Method 1: Try to open in Chrome directly
      const chromeUrl = getChromeUrlScheme(currentUrl);
      console.log('[SafariBlockModal] Attempting to open Chrome URL scheme:', chromeUrl);
      window.location.href = chromeUrl;

      // Method 2: Fallback - open in new tab after a short delay
      setTimeout(() => {
        console.log('[SafariBlockModal] Fallback: Opening URL in new tab');
        window.open(currentUrl, '_blank');
      }, 500);

      // Method 3: Show instructions if the above don't work
      setTimeout(() => {
        console.log('[SafariBlockModal] Showing manual instructions toast');
        toast.info(
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="font-medium">Chrome not opened automatically?</p>
            </div>
            <div className="mt-1 text-sm text-dualite-text-secondary">
              <p>Please:</p>
              <ul className="mt-2 space-y-1.5 list-inside list-disc">
                <li>Copy the URL using the button below</li>
                <li>Open Chrome manually</li>
                <li>Paste the URL in Chrome</li>
              </ul>
            </div>
          </div>,
          {
            autoClose: 8000,
            className: 'info-toast-enhanced',
            position: 'bottom-right',
          },
        );
      }, 2000);
    } catch (error) {
      console.error('[SafariBlockModal] Error opening Chrome:', error);

      // If all else fails, just open in new tab
      console.log('[SafariBlockModal] Final fallback: Opening URL in new tab due to error');
      window.open(currentUrl, '_blank');
    }
  };

  const handleDownloadChrome = () => {
    window.open('https://www.google.com/chrome/', '_blank');
  };

  const handleCopyUrl = async () => {
    const currentUrl = window.location.href;
    console.log('[SafariBlockModal] handleCopyUrl called with URL:', currentUrl);

    try {
      // Try modern clipboard API first

      if (navigator.clipboard && navigator.clipboard.writeText) {
        console.log('[SafariBlockModal] Using modern clipboard API');
        await navigator.clipboard.writeText(currentUrl);
        console.log('[SafariBlockModal] URL successfully copied via clipboard API');
        toast.success(
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="font-medium">✅ URL copied to clipboard!</p>
            </div>
            <div className="mt-1 text-sm text-dualite-text-secondary">
              <p>Please:</p>
              <ul className="mt-2 space-y-1.5 list-inside list-disc">
                <li>Open Chrome</li>
                <li>Paste the URL in Chrome</li>
                <li>Press Enter</li>
              </ul>
            </div>
          </div>,
          {
            autoClose: 6000,
            className: 'success-toast-enhanced',
            position: 'bottom-right',
          },
        );
      } else {
        // Fallback for older browsers
        console.log('[SafariBlockModal] Modern clipboard API not available, using fallback');
        await copyUrlFallback(currentUrl);
      }
    } catch (error) {
      console.error('[SafariBlockModal] Copy failed:', error);
      console.log('[SafariBlockModal] Falling back to copyUrlFallback due to error');
      await copyUrlFallback(currentUrl);
    } finally {
    }
  };

  const copyUrlFallback = async (url: string): Promise<void> => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        toast.success(
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="font-medium">✅ URL copied to clipboard!</p>
            </div>
            <div className="mt-1 text-sm text-dualite-text-secondary">
              <p>Please:</p>
              <ul className="mt-2 space-y-1.5 list-inside list-disc">
                <li>Open Chrome</li>
                <li>Paste the URL in Chrome's address bar</li>
                <li>Press Enter</li>
              </ul>
            </div>
          </div>,
          {
            autoClose: 6000,
            className: 'success-toast-enhanced',
            position: 'bottom-right',
          },
        );
      } else {
        toast.error(
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="font-medium">❌ Failed to copy URL automatically</p>
            </div>
            <div className="mt-1 text-sm text-dualite-text-secondary">
              <p>Please manually copy this URL:</p>
              <code className="mt-2 block p-2 bg-neutral-100 dark:bg-neutral-800 rounded text-xs break-all">{url}</code>
            </div>
          </div>,
          {
            autoClose: 10000,
            className: 'error-toast-enhanced',
            position: 'bottom-right',
          },
        );
      }
    } catch (error) {
      console.error('Copy fallback failed:', error);
      toast.error(
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <p className="font-medium">❌ Failed to copy URL automatically</p>
          </div>
          <div className="mt-1 text-sm text-dualite-text-secondary">
            <p>Please manually copy this URL:</p>
            <code className="mt-2 block p-2 bg-neutral-100 dark:bg-neutral-800 rounded text-xs break-all">{url}</code>
          </div>
        </div>,
        {
          autoClose: 10000,
          className: 'error-toast-enhanced',
          position: 'bottom-right',
        },
      );
    }
  };

  // Block all interactions when Safari is detected
  // useEffect(() => {
  //   if (isSafari && onChatRoute) {
  //     console.log('[SafariBlockModal] Blocking interactions for Safari');

  //     // Prevent scrolling
  //     document.body.style.overflow = 'hidden';
  //     document.body.style.position = 'fixed';
  //     document.body.style.width = '100%';

  //     // Prevent any clicks on the background (but allow modal content)
  //     const preventInteraction = (e: Event) => {
  //       // Allow clicks within the modal
  //       const target = e.target as Element;

  //       if (target && target.closest('.safari-block-modal')) {
  //         return true;
  //       }

  //       e.preventDefault();
  //       e.stopPropagation();
  //       return false;
  //     };

  //     // Add event listeners to prevent interaction
  //     const events = ['click', 'keydown', 'touchstart', 'mousedown', 'mouseup', 'contextmenu'];
  //     events.forEach((event) => {
  //       document.addEventListener(event, preventInteraction, true);
  //     });

  //     // Prevent right-click context menu
  //     document.addEventListener('contextmenu', (e) => e.preventDefault());

  //     return () => {
  //       console.log('[SafariBlockModal] Cleaning up interaction blockers');
  //       document.body.style.overflow = '';
  //       document.body.style.position = '';
  //       document.body.style.width = '';
  //       events.forEach((event) => {
  //         document.removeEventListener(event, preventInteraction, true);
  //       });
  //     };
  //   }
  // }, [isSafari, onChatRoute]);

  if (!isSafari || !onChatRoute) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className="safari-block-modal max-w-sm w-full mx-auto rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4"
        style={{ boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)' }}
      >
        {/* Small warning icon and title */}
        <div className="flex items-center gap-2 mb-2">
          <svg
            className="w-5 h-5 text-neutral-700 dark:text-neutral-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01" />
          </svg>
          <span className="text-sm font-semibold text-neutral-900 dark:text-white">{t('modals.safari.title')}</span>
        </div>

        <div className="text-xs text-neutral-700 dark:text-neutral-300 mb-3 leading-snug">
          {t('modals.safari.message')}
        </div>

        <div className="flex flex-col gap-2 mb-3 ">
          {/* <button
            onClick={(e) => {
              console.log('[SafariBlockModal] Open in Chrome button clicked');
              e.stopPropagation();
              handleOpenInChrome();
            }}
            className="w-full py-2  text-black bg-white border border-black rounded-xl text-center text-sm font-medium hover:bg-neutral-100 transition-colors"
          >
            Open in Chrome
          </button> */}

          <button
            onClick={(e) => {
              console.log('[SafariBlockModal] Copy URL button clicked');
              e.stopPropagation();
              handleCopyUrl();
            }}
            className="w-full py-2 text-sm text-black bg-white border border-black rounded-xl text-center font-medium hover:bg-neutral-100 transition-colors"
          >
            {t('modals.safari.copyUrl')}
          </button>
        </div>

        <div className="text-[11px] text-neutral-500 dark:text-neutral-400 text-center border-t border-neutral-100 dark:border-neutral-800 pt-2 mt-2">
          {t('modals.safari.needHelp')}{' '}
          <a href="mailto:info@dualite.dev" className="underline text-neutral-700 dark:text-neutral-200">
            info@dualite.dev
          </a>
        </div>
      </div>
    </div>
  );
}
